import { useLocales } from '@src/locales'
import { useMemo } from 'react'
import { useSettingsContext } from '@src/components/settings'
import merge from 'lodash/merge'
import { experimental_extendTheme as extendTheme, ThemeOptions } from '@mui/material/styles'
import { palette } from './default/palette'
import { createPresets } from './default/options/presets'
import { createContrast } from './default/options/contrast'
import { customShadows } from './default/custom-shadows'
import { componentsOverrides } from './default/overrides'
import { typography } from './default/typography'
import { shadows } from './default/shadows'

export default function useDefaultTheme() {
  const { currentLang } = useLocales()

  const settings = useSettingsContext()

  const presets = createPresets(settings.themeColorPresets)

  const contrast = createContrast(settings.themeContrast, settings.themeMode)

  const memoizedValue = useMemo(
    () => ({
      colorSchemes: {
        dark: {
          palette: {
            ...palette('dark'),
            ...presets.palette,
            ...contrast.palette,
          },
        },
        light: {
          palette: {
            ...palette('light'),
            ...presets.palette,
            ...contrast.palette,
          },
        },
      },
      customShadows: {
        ...customShadows(settings.themeMode),
        ...presets.customShadows,
      },
      direction: settings.themeDirection,
      shadows: shadows(settings.themeMode),
      shape: { borderRadius: 8 },
      typography,
      themeLayout: settings.themeLayout,
    }),
    [
      presets.palette,
      presets.customShadows,
      contrast.palette,
      settings.themeMode,
      settings.themeLayout,
      settings.themeDirection,
    ],
  )

  const newTheme = extendTheme(memoizedValue as unknown as ThemeOptions)
  newTheme.components = merge(
    componentsOverrides(newTheme),
    contrast.components,
    currentLang.systemValue,
  )
  return newTheme
}
